import { DataService } from "./../services/data.service";
import { observable, computed, action, autorun, toJS } from "mobx";
import { Injectable } from "@angular/core";
import { AuthService } from "../auth/auth.service";
import { MappingService, pushToObject } from "../services/mapping.service";
import { Router } from "@angular/router";
import { ConvertService } from "../services/convert.service";
import { IOptions } from '../interfaces/options';
import { userRolesObj } from '../dummy/status';

@Injectable({providedIn:'root'})
export class Environment {
  @observable user = null;
  @observable dailyShift = null;
  @observable sameDay = false;
  @observable config = null;
  @observable loading = false;
  @observable academic = null;
  @observable process = false;
  @observable balance = null;
  @observable abc = null;
  @observable abcFee = null;
  @observable public term = null;
  @observable public campus = null;
  @observable public puc_generation = null;
  @observable public instituteTerm = null;

  constructor(private ds: DataService,
    private router: Router,
    private auth: AuthService) {
  }

  @action
  async fetchTerm(instituteKey) {
    if (instituteKey) {
      const termDoc = await this.ds.instituteActiveTermRef(instituteKey).get().toPromise();
      const term = MappingService.pushToObject(termDoc);
      this.term = term;
    }
    else {
      const sysConfigDoc = await this.ds.academicRef().get().toPromise();
      const configData = MappingService.pushToObject(sysConfigDoc);
      if (configData) {
        const { abc_course, abc_course_fee, term, puc_generation } = configData;
        this.abc = abc_course;
        this.abcFee = abc_course_fee;
        this.term = term;
        this.puc_generation = puc_generation;
        this.academic = configData;
        this.loading = false;
      }
    }
  }

  @action
  async fetchUser(uid) {
    this.loading = true;
    this.dailyShift = null;
    this.sameDay = false;
    const userDoc = await this.ds.userRefDoc(uid).get().toPromise();
    this.user = MappingService.pushToObject(userDoc);
    if (this.user) {
      const { campus, balance, role, shift } = this.user;
      if (role.key !== userRolesObj.cashier.key) {
        this.auth.authRef().signOut().then(() => {
          this.loading = false;
          this.router.navigate(['/auth']);
        })
      }
      else {
        this.campus = campus;
        this.balance = balance;
        if (shift) {
          const shiftDoc = await this.ds.shiftMovementDocRef(shift.key).get().toPromise();
          this.dailyShift = MappingService.pushToObject(shiftDoc);
          const { create_date_key } = this.dailyShift;
          const dayKey = ConvertService.dateKey();
          if (dayKey == create_date_key) this.sameDay = true;
        }
        const configDoc = await this.ds.sysSetting().get().toPromise();
        const sysConfigDoc = await this.ds.academicRef().get().toPromise();
        this.config = MappingService.pushToObject(configDoc);
        const configData = MappingService.pushToObject(sysConfigDoc);
        if (configData) {
          const { abc_course, abc_course_fee, term, puc_generation } = configData;
          this.abc = abc_course;
          this.abcFee = abc_course_fee;
          this.term = term;
          this.puc_generation = puc_generation;
          this.academic = configData;
          this.loading = false;
        }
      }
    }
    else {
      this.auth.authRef().signOut().then(() => {
        this.loading = false;
        this.router.navigate(['/auth']);
      })
    }
  }

  @action
  clearStore() {
    this.user = null;
    this.dailyShift = null;
    this.config = null;
    this.loading = false;
    this.academic = null;
    this.process = false;
    this.balance = null;
    this.abc = null;
    this.abcFee = null;
    this.term = null;
    this.campus = null;
    this.puc_generation = null;
    this.instituteTerm = null;
  }

  @action
  async activeTermInstitute(instituteKey) {
    const termDoc = await this.ds.instituteActiveTermRef(instituteKey).get().toPromise();
    this.instituteTerm = termDoc.data();
  }

  @action
  getUser(callback) {
    this.loading = true;
    this.user = null;
    const { uid } = this.auth.authRef().currentUser;
    this.ds.userRefDoc(uid).valueChanges().subscribe(doc => {
      this.loading = false;
      this.user = doc;
      callback(doc)
    });
  }

  @action
  async getUserData() {
    this.loading = true;
    const { uid } = this.auth.authRef().currentUser;
    const userDoc = await this.ds.userRefDoc(uid).get().toPromise()
    this.user = pushToObject(userDoc)
  }

  @action
  async fetchDailyShift(cashierKey: string) {
    this.loading = true;
    this.dailyShift = null;
    this.sameDay = false;
    const userDoc = await this.ds.userRefDoc(cashierKey).get().toPromise();
    this.user = MappingService.pushToObject(userDoc);
    const shiftDoc = await this.ds.shiftMovementDocRef(this.user.shift.key).get().toPromise();
    this.dailyShift = MappingService.pushToObject(shiftDoc);
    const { create_date_key } = this.dailyShift;
    const dayKey = ConvertService.dateKey();
    if (dayKey == create_date_key) this.sameDay = true;
    this.loading = false;
  }

  @action
  async fetchConfig() {
    const configDocs = await this.ds.sysSetting().get().toPromise();
    this.config = MappingService.pushToObject(configDocs);
  }

  @action
  async fetchConfigToArray(callback) {
    const configDocs = await this.ds.sysSetting().get().toPromise();
    this.config = MappingService.pushToObject(configDocs);
    callback(this.config)
  }

  @action
  async fetchAcademicConfig() {
    const configDocs = await this.ds.academicRef().get().toPromise();
    const configData = MappingService.pushToObject(configDocs);
    if (configData) {
      const { abc_course, abc_course_fee, term, puc_generation } = configData;
      this.abc = abc_course;
      this.abcFee = abc_course_fee;
      this.term = term;
      this.puc_generation = puc_generation;
      this.academic = configData;
    }
    else {
      this.abc = null;
      this.term = null;
      this.puc_generation = null;
      this.academic = null;
    }
  }

  @action
  async fetchAcademicConfigToArray(callback) {
    const configDocs = await this.ds.academicRef().get().toPromise();
    const configData = MappingService.pushToObject(configDocs);
    const { abc_course, abc_course_fee, term, puc_generation } = configData;
    this.abc = abc_course;
    this.abcFee = abc_course_fee;
    this.term = term;
    this.puc_generation = puc_generation;
    this.academic = configData;
    callback(configData);
  }

  @action
  save(item: IOptions, callback) {
    this.process = true;
    const batch = this.ds.batch();
    batch.update(this.ds.academicFireRef(), {
      abc_course_fee: item.abc_course_fee
    });
    batch.update(this.ds.configFireRef(), {
      modifiedDate: new Date(),
      modifiedBy: null,
      expired_fee_peroid: item.expired_fee_peroid,
      expired_test_peroid: item.expired_test_peroid,
      serial_number: item.serial_number,
      invoice_shufit: item.invoice_shufit,
      first_penalty_fee: item.first_penalty_fee,
      second_penalty_fee: item.second_penalty_fee,
      scholar_fee: item.scholar_fee,
      abc_course_fee: item.abc_course_fee,
      transcript: item.transcript,
      testing_fee: item.testing_fee,
      uat_curriculum: item.uat_curriculum,
      uat_institute: item.uat_institute,
      uat_fee: item.uat_fee,
      academic_fee: item.academic_fee,
      uep_fee: item.uep_fee,
      foreigner_student: item.foreigner_student,
      local_student: item.local_student
    });

    batch.commit().then(() => {
      this.process = false;
      callback(true, null);
    })
      .catch(error => {
        this.process = false;
        callback(false, error);
      });
  }
}
